
import { Component, Prop, Vue } from 'vue-property-decorator'
import { Action, Getter, State } from 'vuex-class'

import type { Account, AccountSettings } from '@/client/users'
import { AccountAccountTierEnum } from '@/client/users'
import authRoutes from '@/modules/auth/modules/routes'
import type { Subscription } from '@/modules/billing/types/Subscription'
import type { IUser } from '@/modules/user/types'

import AppLogo from './AppLogo.vue'
import AppCreateNewDialog from './dialogs/AppCreateNewDialog.vue'

const namespaces = {
  user: 'user',
  accountSettings: 'accountSettings',
  app: 'app'
}

@Component({
  components: {
    AppLogo,
    AppCreateNewDialog
  }
})
export default class AppDrawer extends Vue {
  @State('darkMode', { namespace: namespaces.app }) darkMode?: boolean
  @State('subscription', { namespace: 'user' }) subscription!: Subscription
  @State('user', { namespace: namespaces.user }) user!: IUser
  @State('accountSettings', { namespace: namespaces.accountSettings })
    accountSettings!: AccountSettings

  @Getter('isAdmin', { namespace: namespaces.user }) isAdmin!: boolean
  @Getter('isViewer', { namespace: namespaces.user }) isViewer!: boolean
  @Getter('isSysAdmin', { namespace: namespaces.user }) isSysAdmin!: boolean
  @Getter('currentAccount', { namespace: namespaces.user })
    currentAccount!: Account

  @Action('getAccountSettings', { namespace: namespaces.accountSettings })
    getAccountSettings!: () => Promise<void>

  @Prop({ default: true }) readonly value!: boolean
  @Prop({ default: true }) readonly mini!: boolean

  get drawer () {
    return this.value
  }

  // set drawer (value) {
  //   this.$emit('update-toggle', value)
  // }

  showCreateDialog = false
  async beforeMount () {
    await Promise.all([
      this.accountSettings ? null : this.getAccountSettings()
    ])
  }

  get showSubRoute () {
    const res =
      !!this.user ||
      [
        '404',
        ...authRoutes.flatMap((r) => r.children || r).map(({ name }) => name)
      ].includes(this.$route.name || undefined)
    return res
  }

  get resourcesMenu () {
    return [
      this.isResourcesEnabled
        ? {
            name: 'resources',
            active: true,
            subItems: [
              {
                name: 'funding',
                icon: 'mdi-hand-coin-outline',
                disabled: false
              },
              { name: 'bookkeeper', icon: 'mdi-book-account', disabled: false },
              { name: 'listMyBusiness', icon: 'mdi-domain', disabled: false },
              { name: 'fr8Competition', icon: 'mdi-trophy-outline', disabled: false }
            ]
          }
        : false
    ].filter(Boolean)
  }

  get managementMenu () {
    return [
      {
        name: 'management',
        active: this.isManagementSectionEnabled,
        upgrade: !this.isManagementSectionEnabled,
        subItems: [
          {
            name: 'suppliers',
            icon: 'mdi-contacts-outline',
            disabled: !this.isManagementSectionEnabled
          },
          {
            name: 'products',
            icon: 'mdi-book-outline',
            disabled: !this.isManagementSectionEnabled
          },
          {
            name: 'invoices',
            icon: 'mdi-folder-outline',
            disabled: !this.isManagementSectionEnabled
          },
          this.isOrdersAndShippingEnabled
            ? {
                name: 'orders',
                icon: 'mdi-orders-outline',
                disabled: !this.isManagementSectionEnabled
              }
            : null,
          {
            name: 'bills',
            icon: 'mdi-currency-usd',
            disabled: !this.isManagementSectionEnabled
          }
        ].filter(Boolean)
      }
    ]
  }

  get marketplacesMenu () {
    return [
      this.isMarketPlacesEnabled
        ? {
            name: 'tools',
            active: true,
            subItems: [
              { name: 'analytics', icon: 'mdi-poll', disabled: false },
              {
                name: 'smartTariffClassifier',
                icon: 'mdi-percent-box-outline',
                disabled: false
              },
              this.isFeeTrackerActive
                ? { name: 'feeTrackerPro', icon: 'mdi-feetracker-outline' }
                : null,
              this.isSv1Enabled
                ? { name: 'marketplaceOverview', icon: 'mdi-overview-outline' }
                : null,
              this.isSv1Enabled
                ? { name: 'ServiceFees', icon: 'mdi-servicefees-outline' }
                : null,
              this.isSv1Enabled
                ? { name: 'ProductSales', icon: 'mdi-sales-outline' }
                : null,
              this.isSv1Enabled
                ? {
                    name: 'ProductProfitability',
                    icon: 'mdi-profitability-outline'
                  }
                : null,
              this.isSv1Enabled
                ? {
                    name: 'ProductOrderMetrics',
                    icon: 'mdi-ordermetrics-outline'
                  }
                : null
            ].filter(Boolean)
          }
        : false
    ].filter(Boolean)
  }

  get accountMenu () {
    return [
      {
        name: 'account',
        active: true,
        subItems: [
          { name: 'profile', icon: 'mdi-account' },
          // remove billing section temp
          // this.isAdmin && !this.isBillingLifetime
          //   ? { name: 'billing', icon: 'mdi-credit-card' }
          //   : false,
          this.isAdmin
            ? { name: 'users', icon: 'mdi-account-multiple' }
            : false,
          this.isAdmin
            ? { name: 'integrations', icon: 'mdi-power-plug' }
            : false,
          { name: 'notifications', icon: 'mdi-bell-outline' }
        ].filter(Boolean)
      }
    ]
  }

  get adminMenu () {
    return [
      {
        name: 'system',
        active: true,
        subItems: [
          this.isSysAdmin
            ? { name: 'userManagement', icon: 'mdi-account-group' }
            : null
        ].filter(Boolean)
      }
    ]
  }

  get navigationItems () {
    return [
      ...this.resourcesMenu,
      ...this.managementMenu,
      ...this.accountMenu,
      ...this.adminMenu
    ]
  }

  get isOrdersAndShippingEnabled (): boolean {
    return this.accountSettings?.isOrdersAndShippingEnabled || false
  }

  get isMarketPlacesEnabled (): boolean {
    return this.accountSettings?.isMarketPlacesEnabled || false
  }

  get isBillingLifetime (): boolean {
    return (
      this.currentAccount.accountTier === AccountAccountTierEnum.Lifetime ||
      false
    )
  }

  get isManagementSectionEnabled (): boolean {
    return this.accountSettings?.isManagementSectionEnabled || false
  }

  get isFeeTrackerActive (): boolean {
    return this.accountSettings?.isFeeTrackerActive || false
  }

  get isSv1Enabled (): boolean {
    return this.accountSettings?.isSv1Enabled || false
  }

  get isResourcesEnabled (): boolean {
    return this.accountSettings?.isResourcesEnabled || false
  }

  get supportEmail (): string {
    return process.env?.VUE_APP_SUPPORT_EMAIL || ''
  }
}
